import { AesDecrypt, AesEncrypt, keyPromise } from '../../../../Service/crypto';
import api from '../../../../api';

export interface FieldEntry {
  field_name: string;
  instruction?: string;
}

export type SectionFields = Record<string, (FieldEntry | string)[]>;

// Fetch master fields and filter them by the selected client's assigned `selectedFields`
// exactly as implemented in NewTemplateCreation.tsx
export async function fetchMasterFields(clientId?: string | null): Promise<SectionFields> {
  if (!clientId) return {};
  const key = await keyPromise;
  const userId = localStorage.id;

  try {
    // 1. Fetch client details to get client's `selectedFields`
    const clientResponse = await api
      .fetch('api/clientmanagement/get_client', { method: 'GET' })
      .then((r) => r.json());
    const fullClientsList = await AesDecrypt(key, clientResponse);

    const currentClient = Array.isArray(fullClientsList)
      ? fullClientsList.find((c: any) => c._id === clientId || c.id === clientId)
      : null;

    const selectedFieldsRaw = currentClient?.selectedFields;
    const selectedFieldNames: string[] = [];

    if (selectedFieldsRaw && typeof selectedFieldsRaw === 'object' && !Array.isArray(selectedFieldsRaw)) {
      Object.values(selectedFieldsRaw).forEach((sectionFields: any) => {
        if (Array.isArray(sectionFields)) {
          sectionFields.forEach((f: any) => {
            if (typeof f === 'object' && f.field_name) {
              selectedFieldNames.push(f.field_name);
            } else if (typeof f === 'string') {
              selectedFieldNames.push(f);
            }
          });
        }
      });
    } else if (Array.isArray(selectedFieldsRaw)) {
      selectedFieldsRaw.forEach((f: any) => {
        if (typeof f === 'object' && f.field_name) {
          selectedFieldNames.push(f.field_name);
        } else if (typeof f === 'string') {
          selectedFieldNames.push(f);
        }
      });
    }

    if (selectedFieldNames.length === 0) {
      return {};
    }

    // 2. Fetch master fields schema
    const EncParams = await AesEncrypt(key, userId);
    const response = await api
      .fetch('api/TemplateCreation/get_patient_details', {
        method: 'POST',
        body: JSON.stringify({ EncParams }),
      })
      .then((r) => r.json());
    const decrypted = await AesDecrypt(key, response);

    if (Array.isArray(decrypted) && decrypted[0] && typeof decrypted[0] === 'object') {
      const masterData = decrypted[0];
      const filteredResult: SectionFields = {};

      Object.entries(masterData).forEach(([sectionKey, sectionValue]) => {
        const fields = Array.isArray(sectionValue) ? sectionValue : Object.values(sectionValue as any);
        const filteredFields = fields.filter((field: any) => {
          const fieldName = typeof field === 'object' ? field.field_name : field;
          return selectedFieldNames.includes(fieldName);
        });

        if (filteredFields.length > 0) {
          filteredResult[sectionKey] = filteredFields as (FieldEntry | string)[];
        }
      });

      return filteredResult;
    }
  } catch (error) {
    console.error('Error fetching client master fields:', error);
  }

  return {};
}

export function findFieldInstruction(data: SectionFields, fieldKey: string): { sectionKey: string; instruction: string } | null {
  for (const [sectionKey, fields] of Object.entries(data)) {
    const list = Array.isArray(fields) ? fields : [];
    for (const f of list) {
      const name = typeof f === 'object' ? f.field_name : f;
      if (name === fieldKey) {
        return { sectionKey, instruction: typeof f === 'object' ? f.instruction || '' : '' };
      }
    }
  }
  return null;
}
